import { useCallback, useEffect, useState } from 'react';
import { adminApi } from '../auth.ts';
import type { ListResponse, ReservationDetailData, ReservationListItem } from './types.ts';
import { useDebounce } from './useDebounce.ts';

/** Debounced reservation lookup by name / phone / e-mail / reference. */
export function useReservationSearch(query: string, limit = 8) {
  const [items, setItems] = useState<ReservationListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const debounced = useDebounce(query.trim(), 220);

  useEffect(() => {
    if (debounced.length < 2) {
      setItems([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    adminApi<ListResponse>(
      `/admin/reservations?q=${encodeURIComponent(debounced)}&pageSize=${limit}`,
    )
      .then((d) => !cancelled && setItems(d.items))
      .catch(() => !cancelled && setItems([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [debounced, limit]);

  return { items, loading, hasQuery: debounced.length >= 2 };
}

type DetailState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: ReservationDetailData };

/** Load a single reservation by id. Pass null to stay idle. */
export function useReservationDetail(id: string | null) {
  const [state, setState] = useState<DetailState>({ status: 'idle' });

  const load = useCallback(async (target: string) => {
    setState({ status: 'loading' });
    try {
      const data = await adminApi<ReservationDetailData>(`/admin/reservations/${target}`);
      setState({ status: 'ready', data });
    } catch (err) {
      setState({ status: 'error', message: (err as Error).message });
    }
  }, []);

  useEffect(() => {
    if (!id) {
      setState({ status: 'idle' });
      return;
    }
    load(id);
  }, [id, load]);

  const reload = useCallback(() => {
    if (id) load(id);
  }, [id, load]);

  return { state, reload };
}

/** Resolve a reservation id from a scanned reference code (OKN-XXXX). */
export async function findByReference(reference: string): Promise<string | null> {
  const d = await adminApi<ListResponse>(
    `/admin/reservations?q=${encodeURIComponent(reference)}&pageSize=1`,
  );
  const exact = d.items.find((i) => i.referenceCode === reference) ?? d.items[0];
  return exact?.id ?? null;
}
